import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CalendarClock, Trash2, CheckCircle2, Clock, Zap } from "lucide-react";

const STATUS_STYLE = {
  pending: { text: "text-amber-400", bg: "bg-amber-500/10", icon: Clock },
  sent: { text: "text-emerald-400", bg: "bg-emerald-500/10", icon: CheckCircle2 },
  due: { text: "text-blue-400", bg: "bg-blue-500/10", icon: Zap },
  failed: { text: "text-rose-400", bg: "bg-rose-500/10", icon: Clock },
  cancelled: { text: "text-muted-foreground", bg: "bg-secondary", icon: Clock },
};

export default function Followups() {
  const qc = useQueryClient();
  const [leadId, setLeadId] = useState("");
  const [date, setDate] = useState("");
  const [channel, setChannel] = useState("sms");
  const [message, setMessage] = useState("");

  const { data: followups = [] } = useQuery({
    queryKey: ["followups"],
    queryFn: () => api.get("/followups").then((r) => r.data),
  });
  const { data: leads = [] } = useQuery({
    queryKey: ["leads"],
    queryFn: () => api.get("/leads").then((r) => r.data),
  });

  const create = async () => {
    if (!leadId || !date) { toast.error("Choose a lead and a date"); return; }
    try {
      await api.post("/followups", {
        lead_id: leadId,
        scheduled_at: new Date(date).toISOString(),
        channel,
        message,
        auto: channel === "sms",
      });
      toast.success("Follow-up scheduled");
      setLeadId(""); setDate(""); setMessage("");
      qc.invalidateQueries();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/followups/${id}`);
    qc.invalidateQueries({ queryKey: ["followups"] });
  };

  return (
    <div data-testid="followups-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Follow-ups</h1>
        <p className="text-muted-foreground mt-1">Automated SMS follow-ups fire on schedule so no lead goes cold.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="rounded-2xl border border-border bg-card p-6 space-y-4 h-fit" data-testid="followup-form">
          <h3 className="font-display font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Schedule new</h3>
          <div className="space-y-2">
            <Label>Lead</Label>
            <Select value={leadId} onValueChange={setLeadId}>
              <SelectTrigger data-testid="followup-lead-select"><SelectValue placeholder="Choose a lead" /></SelectTrigger>
              <SelectContent className="bg-popover border-border max-h-64">
                {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>When</Label>
            <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="followup-when-input" />
          </div>
          <div className="space-y-2">
            <Label>Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger data-testid="followup-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="sms">Automated SMS</SelectItem>
                <SelectItem value="call">Call reminder</SelectItem>
                <SelectItem value="reminder">General reminder</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Use {name}. Blank = default message." data-testid="followup-message" />
          </div>
          <Button onClick={create} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="create-followup-button">
            Schedule follow-up
          </Button>
        </div>

        <div className="lg:col-span-2 space-y-3">
          {followups.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
              No follow-ups scheduled yet.
            </div>
          )}
          {followups.map((f) => {
            const st = STATUS_STYLE[f.status] || STATUS_STYLE.pending;
            const Icon = st.icon;
            return (
              <div key={f.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-4" data-testid={`followup-${f.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${st.bg}`}>
                    <Icon className={`h-5 w-5 ${st.text}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{f.lead_name} <span className="text-xs text-muted-foreground capitalize">· {f.channel}</span></p>
                    <p className="text-xs text-muted-foreground truncate">{f.message || "Default follow-up message"}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{fmtDate(f.scheduled_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-md capitalize ${st.bg} ${st.text}`}>{f.status}</span>
                  <Button size="icon" variant="ghost" onClick={() => remove(f.id)} data-testid={`delete-followup-${f.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-400" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
